<template>
  <v-text-field
    :model-value="displayValue"
    label="Edad"
    variant="outlined"
    density="compact"
    type="text"
    inputmode="numeric"
    autocomplete="off"
    :hide-details="hideDetails"
    :readonly="readonlyProp"
    :disabled="disabledProp"
    :filled="filledProp"
    :rules="readonlyProp ? [] : rulesProp"
    :tabindex="tabIndexProp"
    class="edad-text-field"
    :class="{ 'edad-text-field--dark': isDarkTheme }"
    @update:model-value="onInput"
  >
    <template #append-inner>
      <span v-if="readonlyProp" class="edad-text-field__unidad-fija text-body-2 text-button">{{ unidadSuffix }}</span>
      <v-menu v-else-if="unidadEdadSelected" location="bottom">
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            variant="text"
            color="primary"
            class="edad-text-field__unidad-btn text-none"
            height="auto"
            min-width="0"
            :disabled="readonlyProp"
          >
            {{ unidadEdadSelected.name }}
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item
            v-for="item in unidadesMedidaEdadList"
            :key="item.id"
            :title="item.name"
            @click="onUnidadEdadChange(item)"
          />
        </v-list>
      </v-menu>
    </template>
  </v-text-field>
</template>

<script>
export default {
  name: 'EdadTextField',
  props: {
    readonlyProp: {
      type: Boolean,
      default: false,
    },
    filledProp: {
      type: Boolean,
      default: false,
    },
    tabIndexProp: {
      type: String,
      default: '0',
    },
    /** Años (o cadena), u objeto `{ edad, unidadMedida }` con código A|M|D. */
    edad: {
      type: [String, Number, Object],
      default: null,
    },
    disabledProp: {
      type: Boolean,
      default: false,
    },
    rulesProp: {
      type: Array,
      default: () => [],
    },
    hideDetails: {
      type: [Boolean, String],
      default: 'auto',
    },
  },
  emits: ['onEdadChange', 'onUnidadEdadChange'],
  data() {
    return {
      edadSelected: '',
      unidadEdadSelected: null,
      unidadesMedidaEdadList: [
        { id: 0, name: 'Años', code: 'A' },
        { id: 1, name: 'Meses', code: 'M' },
        { id: 2, name: 'Días', code: 'D' },
      ],
    }
  },
  computed: {
    displayValue() {
      if (this.readonlyProp) return this.displayFromProp
      return this.edadSelected === '' || this.edadSelected == null ? '' : String(this.edadSelected)
    },
    displayFromProp() {
      const n = this.edad
      if (n == null || n === '') return ''
      if (typeof n === 'string' || typeof n === 'number') return String(n)
      if (typeof n === 'object' && n.edad != null) return String(n.edad)
      return ''
    },
    unidadSuffix() {
      const n = this.edad
      if (typeof n === 'object' && n != null && n.unidadMedida) {
        const u = this.unidadesMedidaEdadList.find((e) => e.code === n.unidadMedida)
        if (u) return u.name
      }
      return 'Años'
    },
    isDarkTheme() {
      const themeName = this.$vuetify?.theme?.global?.name
      const value = themeName && typeof themeName === 'object'
        ? themeName.value
        : themeName
      return value === 'dark'
    },
  },
  watch: {
    edad: {
      immediate: true,
      handler() {
        if (!this.readonlyProp) this.syncFromProp()
      },
    },
  },
  mounted() {
    this.unidadEdadSelected = this.unidadesMedidaEdadList[0]
    if (!this.readonlyProp) this.syncFromProp()
  },
  methods: {
    syncFromProp() {
      const n = this.edad
      if (n == null || n === '') {
        this.edadSelected = ''
        this.unidadEdadSelected = this.unidadesMedidaEdadList[0]
        return
      }
      if (typeof n === 'string' || typeof n === 'number') {
        this.edadSelected = String(n)
        this.unidadEdadSelected = this.unidadesMedidaEdadList[0]
        return
      }
      if (typeof n === 'object' && n.edad != null) {
        this.edadSelected = String(n.edad)
        const u = this.unidadesMedidaEdadList.find((e) => e.code === n.unidadMedida)
        this.unidadEdadSelected = u ?? this.unidadesMedidaEdadList[0]
      }
    },
    onInput(val) {
      if (this.readonlyProp) return
      this.edadSelected = val
      this.$emit('onEdadChange', val)
    },
    onUnidadEdadChange(item) {
      if (this.readonlyProp) return
      if (!item) {
        this.unidadEdadSelected = this.unidadesMedidaEdadList[0]
        this.$emit('onUnidadEdadChange', this.unidadEdadSelected)
        return
      }
      this.unidadEdadSelected = item
      this.$emit('onUnidadEdadChange', item)
    },
  },
}
</script>

<style scoped>
.edad-text-field__unidad-btn {
  margin-top: -2px;
  font-weight: 500;
  min-width: 3.5rem;
}

.edad-text-field__unidad-fija {
  color: #424242 !important;
  font-weight: 700;
  letter-spacing: normal;
  opacity: 1 !important;
}

.edad-text-field--dark :deep(.edad-text-field__unidad-fija) {
  color: rgba(var(--v-theme-on-surface), 0.95) !important;
}
</style>
